import React from "react";
import GraphViewer from "../../components/GraphPlotter/GraphViewer";
import SvgMathLabel from "../../components/diagrams/SvgMathLabel";
import { generateRationalData } from "../../components/GraphPlotter/generators/generateRationalData";
import { getCircleGraphRanges } from "../../components/GraphPlotter/circleGraphRanges";

export function buildRationalQuadrilateralModel([a, b, c]) {
  const f = x => c * (x - b) / (x - a);
  const interceptY = f(0);
  // C illustrates the construction, not the point maximizing the area.
  const cx = 0.45 * b;
  const points = {
    O: { x: 0, y: 0, textPosition: "bottom left" },
    B: { x: b, y: 0, textPosition: "bottom left" },
    C: { x: cx, y: f(cx), textPosition: "top right" },
    D: { x: 0, y: interceptY, textPosition: "middle left" },
    functionLabel: { x: 0.15 * b, y: f(0.15 * b) + 0.06 * interceptY,
      label: "y=f(x)", showDot: false, color: "#ff9800", textPosition: "middle right" },
  };
  Object.values(points).forEach(point => { if (!point.color) point.color = "#a78bfa"; });
  const { xRange, yRange, graphSize } = getCircleGraphRanges({ points: Object.values(points) });
  const rational = generateRationalData(`${c}*(x-${b})/(x-${a})`, ...xRange, ...yRange, 0.025);
  // Keep only the branch belonging to the task's domain x < a.
  const curves = rational.segments.map(segment => {
    const x = segment.x.filter(value => value < a);
    return { x, y: x.map(f), color: "#ff9800", width: 3 };
  }).filter(segment => segment.x.length > 1);
  rational.asymptotes.forEach(value => curves.push({
    x: [value, value], y: yRange, color: "#ff9800", width: 2, dash: "dash",
  }));
  return {
    points, curves, xRange, yRange, graphSize,
    segments: [["O", "B"], ["B", "C"], ["C", "D"], ["D", "O"]]
      .map(([p1, p2]) => ({ p1, p2, color: "#a78bfa", width: 5, showMarkers: false })),
  };
}

export function RationalQuadrilateralDiagram({ data }) {
  const { graphSize, ...model } = buildRationalQuadrilateralModel(data);
  return <figure aria-label="Wykres funkcji f i czworokąt OBCD" style={{ margin: "20px 0", display: "flex", justifyContent: "center" }}>
    <div style={{ width: "100%", maxWidth: graphSize, minWidth: 0 }}>
      <GraphViewer {...model} width={graphSize} height={graphSize} mathLabels />
    </div>
  </figure>;
}

export function buildFlowerbedModel([radius]) {
  const base = 2.8 * radius;
  const height = 2 * radius * base ** 2 / (base ** 2 - 4 * radius ** 2);
  return { radius, base, height, center: { x: 0, y: radius },
    vertices: [{ x: -base / 2, y: 0 }, { x: base / 2, y: 0 }, { x: 0, y: height }] };
}

export function FlowerbedDiagram({ data }) {
  const model = buildFlowerbedModel(data);
  const scale = 280 / model.height;
  const px = x => 190 + scale * x;
  const py = y => 318 - scale * y;
  return <figure aria-label="Trójkątny kwietnik z kołem fontanny stycznym do trzech boków"
    style={{ margin: "20px 0", display: "flex", justifyContent: "center" }}>
    <svg role="img" aria-label="Trójkąt równoramienny o podstawie x i wpisane koło fontanny"
      viewBox="0 0 380 370" style={{ width: "100%", maxWidth: 380, height: "auto" }}>
      <polygon points={model.vertices.map(p => `${px(p.x)},${py(p.y)}`).join(" ")}
        fill="rgba(14,165,233,0.09)" stroke="currentColor" strokeWidth="2.5" />
      <circle cx={px(0)} cy={py(model.radius)} r={scale * model.radius}
        fill="rgba(139,92,246,0.12)" stroke="#8b5cf6" strokeWidth="2.5" strokeDasharray="7 5" />
      <SvgMathLabel x={190} y={342} math="x" />
    </svg>
  </figure>;
}
